import styled from "styled-components";

import { PauseIcon, PlayIcon } from "../../icons/glyphs";
import { IconButton } from "../IconButton";
import { Tooltip } from "../Tooltip";
import { TrackingPlayButton } from "../TrackingPlayButton";
import { SrOnly } from "../foundations/srOnly";
import { annotationStudioText } from "./messages";
import type { AnnotationStudioLocale, TrackingPreviewStatus } from "./types";

const TrackingControlRoot = styled.div`
  display: inline-flex;
  min-width: 0;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.15rem;
`;

export function TrackingPreviewControls({
  status,
  framesSeen,
  errorMessage,
  locale,
  onStart,
  onStop,
}: {
  status: TrackingPreviewStatus;
  framesSeen: number;
  errorMessage?: string;
  locale: AnnotationStudioLocale;
  onStart: () => void;
  onStop: () => void;
}) {
  const running = status === "starting" || status === "running";
  const paused = status === "stopped";
  const frameCount = `${framesSeen} ${annotationStudioText("trackingFrames", locale)}`;
  const statusLabel = annotationStudioText(
    running
      ? status === "starting"
        ? "trackingStarting"
        : "trackingRunning"
      : paused
        ? "trackingStopped"
        : status === "error"
          ? "trackingFailed"
          : status === "complete"
            ? "trackingComplete"
            : "trackingStatus",
    locale,
  );
  const detail =
    running || paused || status === "complete"
      ? frameCount
      : status === "error"
        ? (errorMessage ?? annotationStudioText("operationFailed", locale))
        : undefined;
  const startLabel = annotationStudioText(
    paused
      ? "resumeTracking"
      : status === "complete"
        ? "trackingRunAgain"
        : status === "error"
          ? "trackingRetry"
          : "track",
    locale,
  );
  const startTooltip = `${startLabel} · ${detail ?? annotationStudioText("trackingReadyDescription", locale)}`;

  return (
    <TrackingControlRoot
      data-tracking-control-bar=""
      data-status={status}
      role="region"
      aria-label={annotationStudioText("trackingStatus", locale)}
    >
      <SrOnly role="status" aria-live="polite" aria-atomic="true">
        {statusLabel}
        {detail ? ` · ${detail}` : ""}
      </SrOnly>
      {running ? (
        <Tooltip
          key="tracking-pause"
          content={`${annotationStudioText("pauseTracking", locale)} · ${statusLabel} · ${frameCount}`}
          placement="top"
        >
          <IconButton
            size="md"
            variant="solid"
            tone="warning"
            aria-label={annotationStudioText("pauseTracking", locale)}
            onClick={onStop}
          >
            <PauseIcon aria-hidden="true" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip key="tracking-start" content={startTooltip} placement="top">
          <TrackingPlayButton aria-label={startLabel} onClick={onStart}>
            <PlayIcon aria-hidden="true" />
          </TrackingPlayButton>
        </Tooltip>
      )}
    </TrackingControlRoot>
  );
}
